import { useState, useEffect, useCallback } from 'react';
import { TranslationStrings, translations } from '@/config/translations';
import { SupportedLanguage } from '@/config/site.config';

// LocalStorage ключи
export const STORAGE_KEYS = {
  translations: 'admin_translations',
  seo: 'admin_seo',
  scripts: 'admin_scripts',
  pages: 'admin_pages',
  branding: 'admin_branding',
  site: 'admin_site',
  schema: 'admin_schema',
  faq: 'admin_faq',
  seoIntro: 'admin_seo_intro',
} as const;

export interface SiteSettings {
  siteUrl: string;
}

export interface SEOSettings {
  ogImageUrl: string;
  ogImageTitle: string;
  ogImageDescription: string;
}

export interface ScriptSettings {
  googleAnalyticsCode: string;
  googleAdsenseCode: string;
  customHeadCode: string;
}

export interface PageSettings {
  privacy: {
    [key in SupportedLanguage]: {
      title: string;
      description: string;
      content: string;
    };
  };
  contact: {
    [key in SupportedLanguage]: {
      title: string;
      description: string;
      email: string;
    };
  };
}

export interface BrandingSettings {
  faviconUrl: string;
  logoUrl: string;
}

// Настройки Schema.org микроразметки
export interface SchemaWebApplication {
  name: string;
  description: string;
  applicationCategory: string;
  operatingSystem: string;
  browserRequirements: string;
  price: string;
  priceCurrency: string;
}

export interface SchemaOrganization {
  name: string;
  logoUrl: string;
}

export interface SchemaSettings {
  webApplication: {
    [key in SupportedLanguage]: SchemaWebApplication;
  };
  organization: {
    [key in SupportedLanguage]: SchemaOrganization;
  };
}

// FAQ типы
export interface FAQItem {
  question: string;
  answer: string;
}

export type FAQSettings = {
  [key in SupportedLanguage]: FAQItem[];
};

// SEO Intro типы
export interface SEOIntroItem {
  title: string;
  text: string;
}

export type SEOIntroSettings = {
  [key in SupportedLanguage]: SEOIntroItem;
};

const defaultSEO: SEOSettings = {
  ogImageUrl: '/og-image.jpg',
  ogImageTitle: '',
  ogImageDescription: '',
};

const defaultScripts: ScriptSettings = {
  googleAnalyticsCode: '',
  googleAdsenseCode: '',
  customHeadCode: '',
};

const defaultPages: PageSettings = {
  privacy: {
    en: { title: '', description: '', content: '' },
    ua: { title: '', description: '', content: '' },
    ru: { title: '', description: '', content: '' },
    es: { title: '', description: '', content: '' },
    pt: { title: '', description: '', content: '' },
    fr: { title: '', description: '', content: '' },
    de: { title: '', description: '', content: '' },
  },
  contact: {
    en: { title: '', description: '', email: 'support@collageeditor.com' },
    ua: { title: '', description: '', email: 'support@collageeditor.com' },
    ru: { title: '', description: '', email: 'support@collageeditor.com' },
    es: { title: '', description: '', email: 'support@collageeditor.com' },
    pt: { title: '', description: '', email: 'support@collageeditor.com' },
    fr: { title: '', description: '', email: 'support@collageeditor.com' },
    de: { title: '', description: '', email: 'support@collageeditor.com' },
  },
};

const defaultBranding: BrandingSettings = {
  faviconUrl: '/favicon.ico',
  logoUrl: '',
};

const defaultSite: SiteSettings = {
  siteUrl: 'https://collageeditor.com',
};

const defaultSchemaWebApp: SchemaWebApplication = {
  name: '',
  description: '',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'All',
  browserRequirements: 'Requires JavaScript',
  price: '0',
  priceCurrency: 'USD',
};

const defaultSchemaOrg: SchemaOrganization = {
  name: '',
  logoUrl: '',
};

const defaultSchema: SchemaSettings = {
  webApplication: {
    en: { ...defaultSchemaWebApp },
    ua: { ...defaultSchemaWebApp },
    ru: { ...defaultSchemaWebApp },
    es: { ...defaultSchemaWebApp },
    pt: { ...defaultSchemaWebApp },
    fr: { ...defaultSchemaWebApp },
    de: { ...defaultSchemaWebApp },
  },
  organization: {
    en: { ...defaultSchemaOrg },
    ua: { ...defaultSchemaOrg },
    ru: { ...defaultSchemaOrg },
    es: { ...defaultSchemaOrg },
    pt: { ...defaultSchemaOrg },
    fr: { ...defaultSchemaOrg },
    de: { ...defaultSchemaOrg },
  },
};

// Дефолтные FAQ вопросы для каждого языка
const defaultFAQRu: FAQItem[] = [
  { question: 'Нужно ли устанавливать программу?', answer: 'Нет, сервис работает полностью онлайн в браузере.' },
  { question: 'Где сохраняются мои фотографии?', answer: 'Фотографии обрабатываются в браузере и не сохраняются на сервере.' },
  { question: 'Можно ли сделать коллаж бесплатно?', answer: 'Да, вы можете пользоваться базовыми функциями бесплатно.' },
  { question: 'Нужна ли регистрация?', answer: 'Нет, регистрация не обязательна.' },
  { question: 'На каких устройствах работает сайт?', answer: 'Сайт работает на компьютерах, планшетах и смартфонах.' },
];

const defaultFAQEn: FAQItem[] = [
  { question: 'Do I need to install a program?', answer: 'No, the service works completely online in your browser.' },
  { question: 'Where are my photos saved?', answer: 'Photos are processed in the browser and are not saved on the server.' },
  { question: 'Can I make a collage for free?', answer: 'Yes, you can use the basic features for free.' },
  { question: 'Is registration required?', answer: 'No, registration is not required.' },
  { question: 'What devices does the site work on?', answer: 'The site works on computers, tablets and smartphones.' },
];

const defaultFAQUa: FAQItem[] = [
  { question: 'Чи потрібно встановлювати програму?', answer: 'Ні, сервіс працює повністю онлайн у браузері.' },
  { question: 'Де зберігаються мої фотографії?', answer: 'Фотографії обробляються у браузері і не зберігаються на сервері.' },
  { question: 'Чи можна зробити колаж безкоштовно?', answer: 'Так, ви можете користуватися базовими функціями безкоштовно.' },
  { question: 'Чи потрібна реєстрація?', answer: "Ні, реєстрація не обов'язкова." },
  { question: 'На яких пристроях працює сайт?', answer: 'Сайт працює на комп\'ютерах, планшетах і смартфонах.' },
];

const defaultFAQEs: FAQItem[] = [
  { question: '¿Necesito instalar un programa?', answer: 'No, el servicio funciona completamente en línea en tu navegador.' },
  { question: '¿Dónde se guardan mis fotos?', answer: 'Las fotos se procesan en el navegador y no se guardan en el servidor.' },
  { question: '¿Puedo hacer un collage gratis?', answer: 'Sí, puedes usar las funciones básicas de forma gratuita.' },
  { question: '¿Es necesario registrarse?', answer: 'No, el registro no es obligatorio.' },
  { question: '¿En qué dispositivos funciona el sitio?', answer: 'El sitio funciona en computadoras, tabletas y teléfonos inteligentes.' },
];

const defaultFAQPt: FAQItem[] = [
  { question: 'Preciso instalar um programa?', answer: 'Não, o serviço funciona completamente online no seu navegador.' },
  { question: 'Onde minhas fotos são salvas?', answer: 'As fotos são processadas no navegador e não são salvas no servidor.' },
  { question: 'Posso fazer uma colagem de graça?', answer: 'Sim, você pode usar os recursos básicos gratuitamente.' },
  { question: 'É necessário se registrar?', answer: 'Não, o registro não é obrigatório.' },
  { question: 'Em quais dispositivos o site funciona?', answer: 'O site funciona em computadores, tablets e smartphones.' },
];

const defaultFAQFr: FAQItem[] = [
  { question: 'Dois-je installer un programme?', answer: 'Non, le service fonctionne entièrement en ligne dans votre navigateur.' },
  { question: 'Où mes photos sont-elles enregistrées?', answer: 'Les photos sont traitées dans le navigateur et ne sont pas enregistrées sur le serveur.' },
  { question: 'Puis-je faire un collage gratuitement?', answer: 'Oui, vous pouvez utiliser les fonctionnalités de base gratuitement.' },
  { question: 'L\'inscription est-elle obligatoire?', answer: 'Non, l\'inscription n\'est pas obligatoire.' },
  { question: 'Sur quels appareils le site fonctionne-t-il?', answer: 'Le site fonctionne sur ordinateurs, tablettes et smartphones.' },
];

const defaultFAQDe: FAQItem[] = [
  { question: 'Muss ich ein Programm installieren?', answer: 'Nein, der Service funktioniert vollständig online in Ihrem Browser.' },
  { question: 'Wo werden meine Fotos gespeichert?', answer: 'Fotos werden im Browser verarbeitet und nicht auf dem Server gespeichert.' },
  { question: 'Kann ich eine Collage kostenlos erstellen?', answer: 'Ja, Sie können die Grundfunktionen kostenlos nutzen.' },
  { question: 'Ist eine Registrierung erforderlich?', answer: 'Nein, eine Registrierung ist nicht erforderlich.' },
  { question: 'Auf welchen Geräten funktioniert die Seite?', answer: 'Die Seite funktioniert auf Computern, Tablets und Smartphones.' },
];

const defaultFAQ: FAQSettings = {
  en: [...defaultFAQEn],
  ua: [...defaultFAQUa],
  ru: [...defaultFAQRu],
  es: [...defaultFAQEs],
  pt: [...defaultFAQPt],
  fr: [...defaultFAQFr],
  de: [...defaultFAQDe],
};

// Дефолтные SEO Intro для каждого языка
const defaultSEOIntro: SEOIntroSettings = {
  en: {
    title: 'Online Collage — Fast and Free',
    text: 'Online collage is a simple way to combine multiple photos into one image right in your browser. Our service allows you to create photo collages online without registration or software installation. Upload photos, choose layouts, and save your finished collage in just a few clicks.'
  },
  ua: {
    title: 'Колаж онлайн — швидко та безкоштовно',
    text: 'Колаж онлайн — це простий спосіб об\'єднати кілька фотографій в одне зображення прямо в браузері. Наш сервіс дозволяє створювати фотоколажі онлайн без реєстрації та встановлення програм. Завантажуйте фото, обирайте макети та зберігайте готовий колаж за кілька кліків.'
  },
  ru: {
    title: 'Коллаж онлайн — быстро и бесплатно',
    text: 'Коллаж онлайн — это простой способ объединить несколько фотографий в одно изображение прямо в браузере. Наш сервис позволяет создавать фотоколлажи онлайн без регистрации и установки программ. Загружайте фото, выбирайте макеты и сохраняйте готовый коллаж за несколько кликов.'
  },
  es: {
    title: 'Collage en línea — rápido y gratis',
    text: 'Collage en línea es una forma sencilla de combinar varias fotos en una sola imagen directamente en tu navegador. Nuestro servicio te permite crear collages de fotos en línea sin registro ni instalación de programas. Sube fotos, elige diseños y guarda tu collage terminado en unos pocos clics.'
  },
  pt: {
    title: 'Colagem online — rápido e grátis',
    text: 'Colagem online é uma maneira simples de combinar várias fotos em uma única imagem diretamente no seu navegador. Nosso serviço permite criar colagens de fotos online sem registro ou instalação de programas. Carregue fotos, escolha layouts e salve sua colagem pronta em poucos cliques.'
  },
  fr: {
    title: 'Collage en ligne — rapide et gratuit',
    text: 'Le collage en ligne est un moyen simple de combiner plusieurs photos en une seule image directement dans votre navigateur. Notre service vous permet de créer des collages photo en ligne sans inscription ni installation de logiciel. Téléchargez des photos, choisissez des mises en page et enregistrez votre collage terminé en quelques clics.'
  },
  de: {
    title: 'Online-Collage — schnell und kostenlos',
    text: 'Online-Collage ist eine einfache Möglichkeit, mehrere Fotos direkt im Browser zu einem Bild zu kombinieren. Unser Service ermöglicht es Ihnen, Fotocollagen online ohne Registrierung oder Programminstallation zu erstellen. Laden Sie Fotos hoch, wählen Sie Layouts und speichern Sie Ihre fertige Collage mit wenigen Klicks.'
  },
};

// Счётчик обновлений для принудительной перерисовки
let updateCounter = 0;

// Получить URL сайта (из настроек или автоматически)
export const getSiteUrl = (): string => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.site);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.siteUrl) return parsed.siteUrl.replace(/\/$/, '');
    }
  } catch (e) {}
  // Фоллбэк на текущий origin
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://yoursite.com';
};

export const loadSite = (): SiteSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.site);
    if (saved) return { ...defaultSite, ...JSON.parse(saved) };
  } catch (e) {
    console.error('Ошибка загрузки настроек сайта:', e);
  }
  return defaultSite;
};

// Функции загрузки (экспортируем для использования в генераторе HTML)
export const loadSEO = (): SEOSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.seo);
    if (saved) return { ...defaultSEO, ...JSON.parse(saved) };
  } catch (e) {
    console.error('Ошибка загрузки SEO:', e);
  }
  return defaultSEO;
};

export const loadScripts = (): ScriptSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.scripts);
    if (saved) return { ...defaultScripts, ...JSON.parse(saved) };
  } catch (e) {
    console.error('Ошибка загрузки скриптов:', e);
  }
  return defaultScripts;
};

export const loadPages = (): PageSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.pages);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        privacy: {
          en: { ...defaultPages.privacy.en, ...parsed.privacy?.en },
          ua: { ...defaultPages.privacy.ua, ...parsed.privacy?.ua },
          ru: { ...defaultPages.privacy.ru, ...parsed.privacy?.ru },
          es: { ...defaultPages.privacy.es, ...parsed.privacy?.es },
          pt: { ...defaultPages.privacy.pt, ...parsed.privacy?.pt },
          fr: { ...defaultPages.privacy.fr, ...parsed.privacy?.fr },
          de: { ...defaultPages.privacy.de, ...parsed.privacy?.de },
        },
        contact: {
          en: { ...defaultPages.contact.en, ...parsed.contact?.en },
          ua: { ...defaultPages.contact.ua, ...parsed.contact?.ua },
          ru: { ...defaultPages.contact.ru, ...parsed.contact?.ru },
          es: { ...defaultPages.contact.es, ...parsed.contact?.es },
          pt: { ...defaultPages.contact.pt, ...parsed.contact?.pt },
          fr: { ...defaultPages.contact.fr, ...parsed.contact?.fr },
          de: { ...defaultPages.contact.de, ...parsed.contact?.de },
        },
      };
    }
  } catch (e) {
    console.error('Ошибка загрузки страниц:', e);
  }
  return defaultPages;
};

export const loadBranding = (): BrandingSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.branding);
    if (saved) return { ...defaultBranding, ...JSON.parse(saved) };
  } catch (e) {
    console.error('Ошибка загрузки брендинга:', e);
  }
  return defaultBranding;
};

export const loadSchema = (): SchemaSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.schema);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        webApplication: {
          en: { ...defaultSchemaWebApp, ...parsed.webApplication?.en },
          ua: { ...defaultSchemaWebApp, ...parsed.webApplication?.ua },
          ru: { ...defaultSchemaWebApp, ...parsed.webApplication?.ru },
          es: { ...defaultSchemaWebApp, ...parsed.webApplication?.es },
          pt: { ...defaultSchemaWebApp, ...parsed.webApplication?.pt },
          fr: { ...defaultSchemaWebApp, ...parsed.webApplication?.fr },
          de: { ...defaultSchemaWebApp, ...parsed.webApplication?.de },
        },
        organization: {
          en: { ...defaultSchemaOrg, ...parsed.organization?.en },
          ua: { ...defaultSchemaOrg, ...parsed.organization?.ua },
          ru: { ...defaultSchemaOrg, ...parsed.organization?.ru },
          es: { ...defaultSchemaOrg, ...parsed.organization?.es },
          pt: { ...defaultSchemaOrg, ...parsed.organization?.pt },
          fr: { ...defaultSchemaOrg, ...parsed.organization?.fr },
          de: { ...defaultSchemaOrg, ...parsed.organization?.de },
        },
      };
    }
  } catch (e) {
    console.error('Ошибка загрузки схемы:', e);
  }
  return defaultSchema;
};

export const loadFAQ = (): FAQSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.faq);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        en: parsed.en || [...defaultFAQEn],
        ua: parsed.ua || [...defaultFAQUa],
        ru: parsed.ru || [...defaultFAQRu],
        es: parsed.es || [...defaultFAQEs],
        pt: parsed.pt || [...defaultFAQPt],
        fr: parsed.fr || [...defaultFAQFr],
        de: parsed.de || [...defaultFAQDe],
      };
    }
  } catch (e) {
    console.error('Ошибка загрузки FAQ:', e);
  }
  return defaultFAQ;
};

export const loadSEOIntro = (): SEOIntroSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.seoIntro);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        en: { ...defaultSEOIntro.en, ...parsed.en },
        ua: { ...defaultSEOIntro.ua, ...parsed.ua },
        ru: { ...defaultSEOIntro.ru, ...parsed.ru },
        es: { ...defaultSEOIntro.es, ...parsed.es },
        pt: { ...defaultSEOIntro.pt, ...parsed.pt },
        fr: { ...defaultSEOIntro.fr, ...parsed.fr },
        de: { ...defaultSEOIntro.de, ...parsed.de },
      };
    }
  } catch (e) {
    console.error('Ошибка загрузки SEO Intro:', e);
  }
  return defaultSEOIntro;
};


export const loadTranslations = (): Record<SupportedLanguage, TranslationStrings> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.translations);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        en: { ...translations.en, ...parsed.en },
        ua: { ...translations.ua, ...parsed.ua },
        ru: { ...translations.ru, ...parsed.ru },
        es: { ...translations.es, ...parsed.es },
        pt: { ...translations.pt, ...parsed.pt },
        fr: { ...translations.fr, ...parsed.fr },
        de: { ...translations.de, ...parsed.de },
      };
    }
  } catch (e) {
    console.error('Ошибка загрузки переводов:', e);
  }
  return {
    en: { ...translations.en },
    ua: { ...translations.ua },
    ru: { ...translations.ru },
    es: { ...translations.es },
    pt: { ...translations.pt },
    fr: { ...translations.fr },
    de: { ...translations.de },
  };
};

// Глобальные слушатели для синхронизации всех хуков
const listeners = new Set<() => void>();

export const triggerSettingsUpdate = () => {
  updateCounter++;
  listeners.forEach(listener => listener());
};

// Хук для реактивных SEO настроек
export const useReactiveSEO = () => {
  const [seo, setSeo] = useState<SEOSettings>(loadSEO);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const update = () => {
      setSeo(loadSEO());
      forceUpdate(c => c + 1);
    };

    listeners.add(update);

    // Слушаем storage events (для других вкладок)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.seo) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return seo;
};

// Хук для реактивных настроек брендинга
export const useReactiveBranding = () => {
  const [branding, setBranding] = useState<BrandingSettings>(loadBranding);

  useEffect(() => {
    const update = () => {
      setBranding(loadBranding());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.branding) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return branding;
};

// Хук для реактивных настроек страниц
export const useReactivePages = () => {
  const [pages, setPages] = useState<PageSettings>(loadPages);

  useEffect(() => {
    const update = () => {
      setPages(loadPages());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.pages) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return pages;
};

// Хук для реактивных переводов
export const useReactiveTranslations = () => {
  const [trans, setTrans] = useState<Record<SupportedLanguage, TranslationStrings>>(loadTranslations);

  useEffect(() => {
    const update = () => {
      setTrans(loadTranslations());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.translations) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return trans;
};

// Хук для реактивных скриптов
export const useReactiveScripts = () => {
  const [scripts, setScripts] = useState<ScriptSettings>(loadScripts);

  useEffect(() => {
    const update = () => {
      setScripts(loadScripts());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.scripts) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return scripts;
};

// Хук для реактивных настроек сайта
export const useReactiveSite = () => {
  const [site, setSite] = useState<SiteSettings>(loadSite);

  useEffect(() => {
    const update = () => {
      setSite(loadSite());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.site) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return site;
};

// Хук для реактивных SEO Intro настроек
export const useReactiveSEOIntro = () => {
  const [seoIntro, setSeoIntro] = useState<SEOIntroSettings>(loadSEOIntro);

  useEffect(() => {
    const update = () => {
      setSeoIntro(loadSEOIntro());
    };

    listeners.add(update);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.seoIntro) {
        update();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(update);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return seoIntro;
};
